import * as React from 'react';

/**
 * Whether an element currently overflows its box horizontally — the question
 * behind "show a tooltip only when the text is actually cut off".
 *
 * Returns `[setRef, isTruncated]`. Pass `setRef` as the element's `ref`: the
 * element is held in state so that mounting, unmounting and remounting it
 * are all re-measure triggers. A ref object cannot do that — its `.current`
 * changing is invisible to effects, so a label hidden by the sidebar's mini
 * bar and shown again under the same text kept a stale answer (F1,
 * docs/review/bugz-6.md).
 *
 * Re-measured whenever `watched` changes (pass the rendered text) and whenever
 * the element's own box changes, via `ResizeObserver`. The element, not the
 * window: the sidebar column is CSS-animated between its mini and full widths,
 * so a label mounted in the same commit as the expand measures against the
 * still-collapsed column (`clientWidth` 0) and every row came up "clipped"
 * until an unrelated window resize. Observing the element delivers the settled
 * width as the last callback of the transition, and it also covers a window
 * resize — the label's box cannot change without it firing — so there is no
 * per-row `resize` listener forcing a reflow on every event (F1/F2,
 * docs/review/bugz-7.md).
 */
export const useIsTruncated = (watched: unknown) => {
  const [el, setEl] = React.useState<HTMLElement | null>(null);
  const [isTruncated, setIsTruncated] = React.useState(false);

  React.useLayoutEffect(() => {
    if (!el) {
      setIsTruncated(false);
      return undefined;
    }
    const measure = () => setIsTruncated(el.scrollWidth > el.clientWidth);
    // Measure once on attach as well: the observer's initial delivery is
    // asynchronous, and a test-runtime stub may never deliver at all.
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
    // `watched` is the re-measure trigger, not a value the effect reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [el, watched]);

  return [setEl, isTruncated] as const;
};
